// お問い合わせ導線の店ごとの表（hooks/contact-redirect.ts が使う）。
// hooks.ts が「この表にある店だけ発動」と判定するため、hook 本体とは別ファイルに置く。
//
// OPP本舗(3) は contact だけ先に移し（Issue #55 の追加指示その2）、残りは Issue #70 の 4 本目で hook ごとの表に足した
// （SITE_COMMON_SITES には入れない。sales-history-price を持たないため）。この表に載っている店だけが contact-redirect の対象。

export interface ContactPages {
  /**
   * おちゃのこ標準の /contact ページ（/contact と /contact/product/N の 2 形）に到達してしまったときの
   * 飛ばし先（任意）。直接 /contact に来た人がおちゃのこ標準のフォームで送信すると基幹（EcSiteForm）に
   * 入らないので、受け口をフリーページ側に一本化する。
   * 旧実装は location.href.indexOf('/contact') で、クエリやフラグメントに '/contact' が入ると誤爆した。
   * ここでは location.pathname で判定する（2026-09-21 田口さん指示・Issue #55）。
   * 11 店中 8 店が旧実装で持っていた分岐。あんしんや(2)・OPP本舗(3) は持っていなかったが同日の指示で足した
   */
  redirect?: string
  /**
   * 付け替え対象のリンク（href の前方一致）。店によって旧ドメインと公開ドメインの 2 つを見る。
   * 任意（top とセット）: b-futou(10) のように /contact に着いてから redirect で飛ばすだけで、リンク自体は
   * 書き換えていない店は設定しない（Issue #70「現行どおり」。公開ドメインのリンクも付け替えるよう広げた
   * 台紙販売(4)・パックジャパン(6) の前例があるが、同じ判断をするなら依頼元に確認してから）
   */
  linkPrefixes?: string[]
  /** リンクの飛び先（linkPrefixes とセット） */
  top?: string
  /**
   * 付け替え対象の form（action の前方一致）。リンクと違って 1 つだけ（現行どおり）。
   * 任意: パックジャパン(6) のように form の付け替えが無い店は設定しない（form を触らない）
   */
  formPrefix?: string
  /** form の action（formPrefix とセット） */
  form?: string
  /** input[value='お問い合わせ'] を押したときの飛び先。任意: 旧実装に jumpContact が無い店は設定しない */
  product?: string
}

export const CONTACT: Record<number, ContactPages> = {
  // ワークアップ fukuro.in（Issue #60。4店目・最後）。★ この店だけ contact まわりが TS 側（FukuroIn/SiteCommon.ts の
  // replaceForm）にあり、おちゃのこの欄には jumpContact() の実体だけがあった。form の付け替えは無い（formPrefix を設定しない）。
  // 2026-09-22 時点の実機: https://www.fukuro.in/contact を指す <a> はトップ・カートで 2 本（ヘッダーの nav_btn）、
  // 商品ページで 3 本（nav_btn 2 + inquiryinput の /contact/product/N）。旧ドメイン opp-fukuro.ocnk.net のリンクは無い。
  // input[value='お問い合わせ'] は他店と同じく現テンプレートに無い（旧実装は window.open で別タブだった）
  1: {
    redirect: 'https://www.fukuro.in/page/183',
    linkPrefixes: ['https://www.fukuro.in/contact'],
    top: '/page/183?from=top',
    product: '/page/183?from=product',
  },
  // あんしんや（Issue #53）
  2: {
    redirect: '/page/34', // 2026-09-21 田口さん指示で追加（旧実装には無かった。Issue #55 のコメント参照）
    linkPrefixes: ['https://www.anshinya.com/contact'],
    top: '/page/34?form=top',
    formPrefix: 'https://www.anshinya.com/contact',
    form: '/page/34',
    product: '/page/34?from=product',
  },
  // OPP本舗（Issue #55 追加指示その2。contact だけの部分移行）。リンクは旧ドメインと公開ドメインの 2 つ、
  // form は旧ドメインだけ（現行どおり）。2026-09-21 時点の商品ページには opp-honpo.ocnk.net/contact/… の <a> が 1 本ある
  3: {
    redirect: '/page/83', // 田口さん指示で追加（旧実装には無かった）
    linkPrefixes: ['https://opp-honpo.ocnk.net/contact', 'https://www.opp-honpo.net/contact'],
    top: '/page/83?from=top',
    formPrefix: 'https://opp-honpo.ocnk.net/contact',
    form: '/page/83',
    product: '/page/83?from=product',
  },
  // 台紙販売（Issue #55）。★ 旧ドメイン daishi.ocnk.net の綴りは現行どおり（www.dansai.jp に揃えない）。
  // 2026-09-21 時点で www.dansai.jp の商品ページには daishi.ocnk.net/contact を指す <a> が 2 本ある（本文中の直書き）。
  // 公開ドメイン側（ヘッダーの「お問い合わせ」と商品ページの inquiryinput）は旧実装が付け替えておらず、
  // redirect 経由で /contact を一度通ってから /page/20 に着いていた。同日の田口さん指示で公開ドメインも足した
  // （旧実装からの意図的な変更。form 側は現行どおり旧ドメインだけ）
  4: {
    redirect: 'https://www.dansai.jp/page/20',
    linkPrefixes: [
      'https://daishi.ocnk.net/contact',
      'https://www.dansai.jp/contact', // 2026-09-21 追加（田口さん指示・Issue #55 追加指示その3）
    ],
    top: 'https://daishi.ocnk.net/page/20?from=top',
    formPrefix: 'https://daishi.ocnk.net/contact',
    form: 'https://daishi.ocnk.net/page/20',
    product: 'https://www.dansai.jp/page/20?from=product',
  },
  // パックジャパン（Issue #58）。contact まわりは TS 側に無く、おちゃのこ「アクセス解析タグ設定」欄の直書きだけだった。
  // 旧実装が付け替えていたのは旧ドメイン pack-japan.ocnk.net のリンクだけで form は無し（formPrefix を設定しない）。
  // 公開ドメイン www.pack-japan.jp/contact のリンク（ヘッダー 2 本 + 商品ページの inquiryinput 1 本）は
  // 台紙販売（#55 追加指示その3）と同じ判断で足した。redirect は旧実装にもあった
  6: {
    redirect: 'https://www.pack-japan.jp/page/20',
    linkPrefixes: ['https://pack-japan.ocnk.net/contact', 'https://www.pack-japan.jp/contact'],
    top: '/page/20?from=top',
    product: '/page/20?from=product',
  },
  // 宅配ビニール袋ドットコム b-futou（Issue #70 の 1 店目）。現行（wuSiteResource2 b-futou/SiteCommon.ts）は
  // /contact に着いたら /page/11 へ飛ばすだけで、リンクの付け替え・form・jumpContact は無い。現行どおり redirect だけ持つ。
  // 2026-09-23 時点の実機: https://www.b-futou.com/contact を指す <a> はトップ・カートに 2 本（ヘッダー）、
  // 商品ページに 3 本（+ inquiryinput の /contact/product/N）。これらは今までどおり /contact を一度通ってから /page/11 に着く
  10: {
    redirect: 'https://www.b-futou.com/page/11',
  },
  // きらきらぷんぷん丸(14) / ボタンマート(15) / Lustia(16)（Issue #70 の 2 本目）。3 店とも b-futou と同形で、
  // 現行（各店 SiteCommon.ts）は /contact に着いたら店のフリーページへ飛ばすだけ。リンクの付け替え・form・jumpContact は無い
  14: {
    redirect: 'https://www.kirakirapunpun-maru.com/page/10',
  },
  15: {
    redirect: 'https://www.button-mart.com/page/19',
  },
  16: {
    redirect: 'https://www.lustia.net/page/6',
  },
  // コミック侍(11) / 紙ストロー(9)（Issue #70 の 3 本目）。b-futou 型 + 公開ドメインのリンク付け替え（旧実装どおり 1 本だけ。
  // 旧ドメイン *.ocnk.net は旧実装が見ていないので足さない）。form・jumpContact は無い。
  // 2026-09-23 実測: コミック侍はトップ 1 本・/page/9 1 本・/page/11 1 本、紙ストローはトップ 2 本・/cart 1 本・/page/9 2 本
  11: {
    redirect: 'https://www.comic-samurai.com/page/11',
    linkPrefixes: ['https://www.comic-samurai.com/contact'],
    top: '/page/11?from=top',
  },
  9: {
    redirect: 'https://www.paper-straw.jp/page/9',
    linkPrefixes: ['https://www.paper-straw.jp/contact'],
    top: '/page/9?from=top',
  },
}

/** contact-redirect を発動する店（この表にある店） */
export const CONTACT_SITES: number[] = Object.keys(CONTACT).map(Number)

/** おちゃのこ標準の問い合わせページか（/contact または /contact/…）。クエリやフラグメントは見ない */
export function isContactPath(pathname: string): boolean {
  return pathname === '/contact' || pathname === '/contact/' || pathname.startsWith('/contact/')
}

/** /contact に到達していたら飛ばし先を返す。飛び先（/page/NN）では発動しないので無限ループしない */
export function contactRedirectTarget(pathname: string, pages: ContactPages): string | null {
  return pages.redirect && isContactPath(pathname) ? pages.redirect : null
}
